import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  getLotes, updateLote, getCategoriasGastos, createCategoriaGasto, getGastos,
  createGasto, updateGasto, deleteGasto, getConfigCostos, updateConfigCostos, getCostosResumen,
} from '../lib/api';
import { formatDate } from '../lib/dateUtils';
import { exportToExcel } from '../lib/exportUtils';

import { CostosHeader } from './costos/CostosHeader';
import { ResumenKPIs } from './costos/ResumenKPIs';
import { DistribucionYConfig } from './costos/DistribucionYConfig';
import { GastosTable } from './costos/GastosTable';
import { RentabilidadPorLote } from './costos/RentabilidadPorLote';
import { ReporteSemanalLotes } from './costos/ReporteSemanalLotes';
import {
  GastoDialog, ConfigCostosDialog, CategoriaDialog, FaseDialog,
} from './costos/CostosDialogs';

const INITIAL_GASTO = {
  categoria: '',
  fecha: new Date().toISOString().split('T')[0],
  descripcion: '',
  monto: '',
  cantidad: '',
  unidad: '',
  precio_unitario: '',
};

export default function CostosROI() {
  const [lotes, setLotes] = useState([]);
  const [selectedLoteId, setSelectedLoteId] = useState('');
  const [categorias, setCategorias] = useState([]);
  const [gastos, setGastos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [gastoOpen, setGastoOpen] = useState(false);
  const [editingGastoId, setEditingGastoId] = useState(null);
  const [deletingGasto, setDeletingGasto] = useState(null);
  const [deletingGastoInProgress, setDeletingGastoInProgress] = useState(false);
  const [gastoForm, setGastoForm] = useState(INITIAL_GASTO);
  const [savingGasto, setSavingGasto] = useState(false);

  const [configOpen, setConfigOpen] = useState(false);
  const [configForm, setConfigForm] = useState({
    valor_granja: '', vida_util_anos: '', num_lotes_depreciar: '',
  });
  const [savingConfig, setSavingConfig] = useState(false);

  const [catOpen, setCatOpen] = useState(false);
  const [catForm, setCatForm] = useState('');

  const [faseOpen, setFaseOpen] = useState(false);
  const [faseForm, setFaseForm] = useState({ fase: '', fecha_inicio_postura: '' });

  const [filtroCategoria, setFiltroCategoria] = useState('all');

  // ============================================================
  // Data
  // ============================================================
  const loadBase = useCallback(async () => {
    try {
      setLoading(true);
      const [lotesRes, catsRes] = await Promise.all([getLotes(), getCategoriasGastos()]);
      setLotes(lotesRes.data);
      setCategorias(catsRes.data);
      if (lotesRes.data.length > 0 && !selectedLoteId) {
        setSelectedLoteId(lotesRes.data[0]._id || lotesRes.data[0].id);
      }
    } catch (err) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadBase(); }, [loadBase]);

  const loadLoteData = useCallback(async () => {
    if (!selectedLoteId) return;
    try {
      const [gastosRes, resumenRes, configRes] = await Promise.all([
        getGastos({ lote_id: selectedLoteId, limit: 200 }),
        getCostosResumen(selectedLoteId),
        getConfigCostos(selectedLoteId),
      ]);
      setGastos(gastosRes.data);
      setResumen(resumenRes.data);
      setConfig(configRes.data);
    } catch (err) {
      console.error('Error loading lote data:', err);
    }
  }, [selectedLoteId]);

  useEffect(() => { loadLoteData(); }, [loadLoteData]);

  // ============================================================
  // Gasto handlers
  // ============================================================
  const openNewGasto = () => {
    setEditingGastoId(null);
    setGastoForm(INITIAL_GASTO);
    setGastoOpen(true);
  };

  const openEditGasto = (g) => {
    setEditingGastoId(g.id || g._id);
    setGastoForm({
      categoria: g.categoria || '',
      fecha: g.fecha?.split('T')[0] || new Date().toISOString().split('T')[0],
      descripcion: g.descripcion || '',
      monto: String(g.monto ?? ''),
      cantidad: g.cantidad != null ? String(g.cantidad) : '',
      unidad: g.unidad || '',
      precio_unitario: g.precio_unitario != null ? String(g.precio_unitario) : '',
    });
    setGastoOpen(true);
  };

  const handleMontocalc = (field, value) => {
    setGastoForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'cantidad' || field === 'precio_unitario') {
        const cant = parseFloat(updated.cantidad) || 0;
        const precio = parseFloat(updated.precio_unitario) || 0;
        if (cant > 0 && precio > 0) {
          updated.monto = String((cant * precio).toFixed(2));
        }
      }
      return updated;
    });
  };

  const handleSaveGasto = async () => {
    if (!gastoForm.categoria || !gastoForm.fecha || !gastoForm.monto) {
      toast.error('Complete categoria, fecha y monto');
      return;
    }
    setSavingGasto(true);
    try {
      const payload = {
        lote_id: selectedLoteId,
        categoria: gastoForm.categoria,
        fecha: gastoForm.fecha,
        descripcion: gastoForm.descripcion,
        monto: parseFloat(gastoForm.monto),
        cantidad: gastoForm.cantidad ? parseFloat(gastoForm.cantidad) : null,
        unidad: gastoForm.unidad || null,
        precio_unitario: gastoForm.precio_unitario ? parseFloat(gastoForm.precio_unitario) : null,
      };
      if (editingGastoId) {
        await updateGasto(editingGastoId, payload);
        toast.success('Gasto actualizado');
      } else {
        await createGasto(payload);
        toast.success('Gasto registrado');
      }
      setGastoOpen(false);
      setEditingGastoId(null);
      loadLoteData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando gasto');
    } finally {
      setSavingGasto(false);
    }
  };

  const handleConfirmDeleteGasto = async () => {
    if (!deletingGasto) return;
    setDeletingGastoInProgress(true);
    try {
      await deleteGasto(deletingGasto.id || deletingGasto._id);
      toast.success('Gasto eliminado');
      setDeletingGasto(null);
      loadLoteData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error eliminando gasto');
    } finally {
      setDeletingGastoInProgress(false);
    }
  };

  const exportarGastosExcel = () => {
    const ok = exportToExcel(
      gastos,
      [
        { label: 'Fecha', accessor: (g) => formatDate(g.fecha) },
        { label: 'Categoría', key: 'categoria' },
        { label: 'Descripción', key: 'descripcion' },
        { label: 'Cantidad', key: 'cantidad' },
        { label: 'Unidad', key: 'unidad' },
        { label: 'Precio Unit.', accessor: (g) => g.precio_unitario != null ? Number(g.precio_unitario).toFixed(2) : '' },
        { label: 'Monto', accessor: (g) => Number(g.monto || 0).toFixed(2) },
      ],
      'gastos_lote',
      'Gastos',
    );
    if (!ok) toast.error('No hay gastos para exportar');
    else toast.success('Excel descargado');
  };

  // ============================================================
  // Config / Categoria / Fase
  // ============================================================
  const openConfig = () => {
    if (config) {
      setConfigForm({
        valor_granja: config.valor_granja ? String(config.valor_granja) : '',
        vida_util_anos: config.vida_util_anos ? String(config.vida_util_anos) : '',
        num_lotes_depreciar: config.num_lotes_depreciar ? String(config.num_lotes_depreciar) : '1',
      });
    }
    setConfigOpen(true);
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await updateConfigCostos({
        lote_id: selectedLoteId,
        valor_granja: parseFloat(configForm.valor_granja) || 0,
        vida_util_anos: parseFloat(configForm.vida_util_anos) || 20,
        num_lotes_depreciar: parseInt(configForm.num_lotes_depreciar, 10) || 1,
      });
      toast.success('Configuracion actualizada');
      setConfigOpen(false);
      loadLoteData();
    } catch (err) {
      toast.error('Error guardando configuracion');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleCreateCategoria = async () => {
    if (!catForm) return;
    try {
      await createCategoriaGasto({ nombre: catForm });
      toast.success('Categoria creada');
      setCatOpen(false);
      setCatForm('');
      const res = await getCategoriasGastos();
      setCategorias(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const openFase = () => {
    const lote = lotes.find((l) => (l.id || l._id) === selectedLoteId);
    setFaseForm({
      fase: lote?.fase || 'Levante',
      fecha_inicio_postura: lote?.fecha_inicio_postura?.split('T')[0]
        || new Date().toISOString().split('T')[0],
    });
    setFaseOpen(true);
  };

  const handleSaveFase = async () => {
    try {
      const data = { fase: faseForm.fase };
      if (faseForm.fase === 'Postura') {
        data.fecha_inicio_postura = faseForm.fecha_inicio_postura;
      }
      await updateLote(selectedLoteId, data);
      toast.success(`Fase actualizada a ${faseForm.fase}`);
      setFaseOpen(false);
      loadBase();
      loadLoteData();
    } catch (err) {
      toast.error('Error actualizando fase');
    }
  };

  // ============================================================
  // Derivados
  // ============================================================
  const filteredGastos = filtroCategoria === 'all'
    ? gastos
    : gastos.filter((g) => g.categoria === filtroCategoria);

  const selectedLote = lotes.find((l) => (l.id || l._id) === selectedLoteId);

  // ============================================================
  // Render
  // ============================================================
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (lotes.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-30" />
        <h2 className="text-lg font-semibold mb-2">No hay lotes registrados</h2>
        <p>Cree un lote primero en Configuracion para comenzar a registrar costos.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <CostosHeader
        lotes={lotes}
        selectedLoteId={selectedLoteId}
        setSelectedLoteId={setSelectedLoteId}
        selectedLote={selectedLote}
        onOpenFase={openFase}
        onOpenCategoria={() => setCatOpen(true)}
        onOpenConfig={openConfig}
        onNewGasto={openNewGasto}
      />

      <ResumenKPIs resumen={resumen} />

      <RentabilidadPorLote />

      <ReporteSemanalLotes />

      <DistribucionYConfig
        resumen={resumen}
        config={config}
        onOpenConfig={openConfig}
      />

      <GastosTable
        gastos={gastos}
        filteredGastos={filteredGastos}
        categorias={categorias}
        filtroCategoria={filtroCategoria}
        setFiltroCategoria={setFiltroCategoria}
        onExportExcel={exportarGastosExcel}
        onEditGasto={openEditGasto}
        onDeleteGasto={setDeletingGasto}
      />

      <GastoDialog
        open={gastoOpen}
        onOpenChange={(o) => { setGastoOpen(o); if (!o) setEditingGastoId(null); }}
        editing={!!editingGastoId}
        form={gastoForm}
        onChange={setGastoForm}
        onMontoCalc={handleMontocalc}
        categorias={categorias}
        saving={savingGasto}
        onSave={handleSaveGasto}
      />

      <ConfigCostosDialog
        open={configOpen}
        onOpenChange={setConfigOpen}
        form={configForm}
        onChange={setConfigForm}
        saving={savingConfig}
        onSave={handleSaveConfig}
      />

      <CategoriaDialog
        open={catOpen}
        onOpenChange={setCatOpen}
        value={catForm}
        onChange={setCatForm}
        onSave={handleCreateCategoria}
      />

      <FaseDialog
        open={faseOpen}
        onOpenChange={setFaseOpen}
        form={faseForm}
        onChange={setFaseForm}
        onSave={handleSaveFase}
      />

      {/* Confirmar eliminar gasto */}
      <AlertDialog open={!!deletingGasto} onOpenChange={(o) => { if (!o) setDeletingGasto(null); }}>
        <AlertDialogContent data-testid="confirmar-eliminar-gasto">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este gasto?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el gasto de{' '}
              <strong>${Number(deletingGasto?.monto || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })}</strong>
              {deletingGasto?.categoria ? ` (${deletingGasto.categoria})` : ''}. El resumen de ROI se actualizará automáticamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingGastoInProgress}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteGasto}
              disabled={deletingGastoInProgress}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="btn-confirmar-eliminar-gasto"
            >
              {deletingGastoInProgress ? 'Eliminando...' : 'Eliminar Gasto'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
